import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  TASK_LABELS,
  TASK_TYPES,
  mondayOf,
  taskCoversDay,
  weekDays,
  type ScheduleTask,
  type ScheduleVessel,
  type TaskType,
} from '@technohull/shared';
import {
  useDeleteScheduleTask,
  useDeleteScheduleVessel,
  useImportSchedule,
  useOverview,
  useSaveScheduleTask,
  useSaveScheduleVessel,
  useSchedule,
} from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { ActionBar, Chip, ChipGroup, ErrorState, Fact, FactEnd, FactSentence, FilterBar, FilterToggle, InlineError, LoadingState, LogoSpin, Modal, type OverflowAction, OverflowActions, PageHeader, SearchInput, Segmented, useConfirm } from '../components/ui.js';
import { IconCheck, IconDownload, IconPrinter, IconUpload } from '../components/icons.js';
import { exportSchedulePdf, exportScheduleXlsx, printSchedule, type SchedRow } from '../features/exportSchedule.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { MQ, useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

// the Monday of this week by the LOCAL day (just after midnight on a Monday it is already the new week)
const thisMonday = () => mondayOf(new Date(`${localTodayIso()}T00:00:00Z`));
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const daySpan = (start: string, end: string) =>
  Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;

function shiftWeek(weekStart: string, deltaDays: number): string {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

// the Week / List choice is remembered per device. With nothing saved, phones open on the List (ticking
// tasks off at the boat) and wider screens on the Week board, as before.
type SchedView = 'week' | 'list';
const VIEW_KEY = 'rolodex.schedule.view';
function initialView(): SchedView {
  try {
    const saved = window.localStorage.getItem(VIEW_KEY);
    if (saved === 'week' || saved === 'list') return saved;
  } catch { /* storage blocked: fall through to the default */ }
  try { return window.matchMedia(MQ.phone).matches ? 'list' : 'week'; } catch { return 'week'; }
}
function rememberView(v: SchedView) {
  try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* storage blocked: the choice lasts this visit */ }
}

const SCHED_CSS = `
/* week navigator */
.sched-weeknav{display:inline-flex;align-items:center;gap:6px;}
.sched-weeknav .wk-range{min-width:168px;text-align:center;font-weight:600;font-size:13px;color:var(--text-strong);font-variant-numeric:tabular-nums;}
.sched-weeknav .wk-now{display:inline-block;font-size:9px;line-height:1;padding:3px 6px;border-radius:999px;background:var(--accent);color:var(--surface);font-weight:700;text-transform:uppercase;letter-spacing:.06em;}
/* the week's headline fact sentence (FactSentence in ui.tsx) */
.sched-facts{margin:2px 0 14px;}
/* grid: dim weekends, stronger today, cleaner multi-day bars, add-on-hover */
.gantt-tbl th.wknd:not(.today){opacity:.72;}
.gantt-tbl td.dcell.wknd:not(.today){background:var(--surface-sunken);}
.gantt-tbl td.dcell{position:relative;}
.gantt-tbl td.dcell .dcell-add{position:absolute;inset:0;display:none;align-items:center;justify-content:center;color:var(--accent);font-size:18px;font-weight:300;opacity:.45;pointer-events:none;}
.gantt-tbl td.dcell:hover .dcell-add{display:flex;}
/* keyboard focus: the global box-shadow ring is lost on table cells and on the pinned vessel column */
.gantt-tbl td.dcell:focus-visible{outline:2px solid var(--accent);outline-offset:-2px;box-shadow:none;}
.gantt-tbl td.dcell:focus-visible .dcell-add{display:flex;}
.tbar:focus-visible{box-shadow:none;outline:2px solid var(--accent);outline-offset:1px;}
.gantt-tbl td.vcell [role=button]:focus-visible{box-shadow:none;outline:2px solid var(--accent);outline-offset:1px;}
/* list view: the task label is the row's keyboard control (the row stays a plain table row) */
.sched-list .sl-open{background:none;border:none;padding:0;margin:0;font:inherit;color:inherit;text-align:left;cursor:pointer;border-radius:var(--radius-sm);}
.tbar.cont{font-size:0;padding:0;height:16px;margin-top:0;border-radius:0;opacity:.9;}
.tbar.r-start{border-top-right-radius:0;border-bottom-right-radius:0;}
.tbar.r-end{border-top-left-radius:0;border-bottom-left-radius:0;}
.tbar.r-mid{border-radius:0;}
/* a bar's notes line and the vessel column's notes (were inline styles; phones enlarge them below) */
.tbar .tbar-note{display:block;font-size:8px;font-weight:400;opacity:.92;line-height:1.2;white-space:normal;}
.gantt-tbl .sched-vnote{font-size:10px;opacity:.72;font-style:italic;margin-top:2px;white-space:normal;}
.wk-range-now{display:none;}

/* List view on phones: tasks as cards under a sticky start-date heading, each with a 44px Done toggle */
.sched-list-cap{display:flex;align-items:center;justify-content:space-between;gap:8px;}
.sched-jump{flex:none;gap:4px;}
.sched-cards,.sched-day-list{list-style:none;margin:0;padding:0;}
.sched-day-hd{position:sticky;top:calc(-1 * var(--pad-y, 0px));z-index:2;display:flex;align-items:center;gap:8px;margin:0 calc(-1 * var(--pad-r, 0px)) 6px calc(-1 * var(--pad-l, 0px));padding:10px var(--pad-r, 12px) 6px var(--pad-l, 12px);background:var(--bg);font-family:var(--font-disp);font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--text-muted);}
.sched-day-now{font-size:10.5px;line-height:1;padding:3px 7px;border-radius:999px;background:var(--accent);color:var(--surface);}
.sched-card{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:8px;margin-bottom:8px;padding-right:10px;background:var(--surface);border:1px solid var(--hairline);border-radius:var(--radius);box-shadow:var(--shadow-sm);}
.sched-card.is-today{background:linear-gradient(var(--accent-weak),var(--accent-weak)),var(--surface);}
.sched-card-main{display:flex;flex-direction:column;gap:3px;min-width:0;padding:11px 0 11px 14px;margin:0;border:0;background:none;font:inherit;color:inherit;text-align:left;border-radius:var(--radius);-webkit-tap-highlight-color:transparent;}
button.sched-card-main{cursor:pointer;}
button.sched-card-main:active{background:color-mix(in srgb,var(--accent) 8%,transparent);}
.sched-card-task{display:flex;align-items:center;gap:8px;font-size:14px;font-weight:600;color:var(--text-strong);}
.sched-card-task .fchip-dot{opacity:1;}
.sched-card-vessel{font-size:14px;color:var(--text);overflow-wrap:anywhere;}
.sched-card-meta{font-size:12px;color:var(--text-muted);font-variant-numeric:tabular-nums;}
.sched-card-notes{font-size:12.5px;font-style:italic;color:var(--text-muted);overflow-wrap:anywhere;}
.sched-card.is-done .sched-card-task{text-decoration:line-through;opacity:.6;}
.sched-done{flex:none;display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;padding:0;border:1.5px solid var(--border);border-radius:var(--radius);background:var(--surface);color:var(--text-faint);cursor:pointer;-webkit-tap-highlight-color:transparent;}
.sched-done[aria-pressed="true"]{background:var(--ok-fg);border-color:var(--ok-fg);color:var(--surface);}
.sched-done:disabled{cursor:default;opacity:.6;}
.sched-done[aria-busy="true"]{opacity:.5;}
.sched-done:focus-visible{outline:none;box-shadow:var(--ring);}

/* Import dialog on phones: one tappable row per vessel instead of the 7-column table */
.imp-list{list-style:none;margin:0;padding:0;border-top:1px solid var(--border-2);}
.imp-row{display:grid;grid-template-columns:44px minmax(0,1fr);align-items:start;padding:8px 0;border-bottom:1px solid var(--border-2);cursor:pointer;}
.imp-row > input{justify-self:center;margin:2px 0 0;}
.imp-row.is-off .imp-body{opacity:.55;}
.imp-body{display:flex;flex-direction:column;gap:2px;min-width:0;}
.imp-name{font-weight:600;font-size:14px;color:var(--text-strong);overflow-wrap:anywhere;}
.imp-meta{font-size:12px;color:var(--text-muted);}
.imp-dates{font-size:11.5px;color:var(--text-muted);font-variant-numeric:tabular-nums;}
.imp-all{margin:0 0 4px;}
.imp-none{padding:20px 0;text-align:center;}

/* ── phones ── the week board stays a real Gantt that scrolls sideways: the vessel column is pinned at
   --pin-w and every day gets 84px, so bars and their labels read; today is scrolled in beside the vessel
   column. The header and import footer reflow; export checkboxes wait for "Select" mode. */
@media (max-width: 640px), (max-height: 500px) and (pointer: coarse) {
  .sched-weeknav{display:flex;flex:1 1 100%;}
  .sched-weeknav .btn{min-height:44px;min-width:44px;justify-content:center;}
  .sched-weeknav .wk-range{flex:1 1 auto;min-width:0;}
  .sched-weeknav .wk-now{display:none;}
  .wk-range-now{display:block;margin-top:2px;font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--accent);}
  .gantt-tbl{--pin-w:132px;width:calc(var(--pin-w) + 7 * 84px);}
  .gantt-tbl th{font-size:11px;}
  .gantt-tbl th.vh,.gantt-tbl td.vcell{width:var(--pin-w);min-width:var(--pin-w);max-width:var(--pin-w);white-space:normal;}
  .gantt-tbl .sched-vnote{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;font-size:11px;}
  .tbar{font-size:11px;min-height:24px;white-space:normal;}
  .tbar .tbar-note{font-size:10px;}
  .sched-board.is-selecting td.dcell .dcell-add{display:none;}
  .sched-chk{display:inline-flex;align-items:center;justify-content:center;flex:none;min-width:44px;min-height:44px;margin:-10px -8px -10px -10px;cursor:pointer;}
  .modal-ft .imp-auto{flex:1 0 100%;min-height:44px;margin-right:0;}
}
/* touch: show the faint "+" an empty day cell adds a task with (desktop shows it on hover) */
@media (hover: none) {
  .gantt-tbl td.dcell[tabindex] .dcell-add{display:flex;opacity:.2;}
}
`;

// ── task modal ────────────────────────────────────────────────────────────────
function TaskModal({
  vesselId,
  task,
  day,
  onClose,
}: {
  vesselId: string;
  task: ScheduleTask | null;
  day: string;
  onClose: () => void;
}) {
  const save = useSaveScheduleTask();
  const del = useDeleteScheduleTask();
  const [type, setType] = useState<TaskType>(task?.type ?? 'teak');
  const [start, setStart] = useState(task?.start ?? day);
  const [end, setEnd] = useState(task?.end ?? day);
  const [notes, setNotes] = useState(task?.notes ?? '');
  const [completed, setCompleted] = useState(task?.completed ?? false);
  const invalid = end < start;

  return (
    <Modal
      title={task ? 'Edit task' : 'Add task'}
      onClose={onClose}
      footer={
        <>
          {task && (
            <button className="btn btn-danger btn-sm" style={{ marginRight: 'auto' }} disabled={del.isPending} onClick={() => del.mutate(task.id, { onSuccess: onClose })}>
              Delete
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={invalid || save.isPending}
            onClick={() => save.mutate({ id: task?.id, vesselId, type, start, end, notes, completed }, { onSuccess: onClose })}
          >
            Save
          </button>
        </>
      }
    >
      {(save.error || del.error) && <InlineError error={save.error ?? del.error} onDismiss={() => { save.reset(); del.reset(); }} />}
      <label className="field-label" htmlFor="st-type">Type</label>
      <select id="st-type" className="field" style={{ width: '100%' }} value={type} onChange={(e) => setType(e.target.value as TaskType)}>
        {TASK_TYPES.map((t) => (
          <option key={t} value={t}>{TASK_LABELS[t]}</option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <div style={{ flex: 1 }}>
          <label className="field-label" htmlFor="st-start">Start</label>
          <input id="st-start" className="field" style={{ width: '100%' }} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label className="field-label" htmlFor="st-end">End</label>
          <input id="st-end" className="field" style={{ width: '100%' }} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      {invalid && <div style={{ color: 'var(--err-fg)', fontSize: 11, marginTop: 4 }}>End must be on or after start.</div>}
      <label className="field-label" htmlFor="st-notes" style={{ marginTop: 10 }}>Notes</label>
      <input id="st-notes" className="field" style={{ width: '100%' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      {/* a 44px row on touch: the tap way to mark a task done (desktop also has double-click on the bar) */}
      <label className="check-row" style={{ display: 'flex', gap: 8, marginTop: 12, fontSize: 12 }}>
        <input type="checkbox" checked={completed} onChange={(e) => setCompleted(e.target.checked)} /> Mark completed
      </label>
    </Modal>
  );
}

// ── vessel modal ──────────────────────────────────────────────────────────────
function VesselModal({ vessel, season, onClose }: { vessel: ScheduleVessel | null; season: string; onClose: () => void }) {
  const save = useSaveScheduleVessel(season);
  const del = useDeleteScheduleVessel();
  const [name, setName] = useState(vessel?.name ?? '');
  const [sap, setSap] = useState(vessel?.sap ?? '');
  const [deliveryDate, setDeliveryDate] = useState(vessel?.deliveryDate ?? '');
  const [notes, setNotes] = useState(vessel?.notes ?? '');
  const [hidden, setHidden] = useState(vessel?.hidden ?? false);
  const [confirmNode, confirm] = useConfirm();

  return (
    <>
      <Modal
        title={vessel ? 'Edit vessel' : 'Add vessel'}
        onClose={onClose}
        footer={
          <>
            {vessel && (
              <button
                className="btn btn-danger btn-sm"
                style={{ marginRight: 'auto' }}
                disabled={del.isPending}
                onClick={async () => {
                  // the worker deletes every task of the vessel with it, so ask first
                  if (await confirm({ title: 'Delete vessel', message: `Delete “${vessel.name}” and all of its scheduled tasks?`, confirmLabel: 'Delete', danger: true }))
                    del.mutate(vessel.id, { onSuccess: onClose });
                }}
              >
                Delete
              </button>
            )}
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={!name.trim() || save.isPending}
              onClick={() => save.mutate({ id: vessel?.id, name, sap, deliveryDate, notes, hidden }, { onSuccess: onClose })}
            >
              Save
            </button>
          </>
        }
      >
        {(save.error || del.error) && <InlineError error={save.error ?? del.error} onDismiss={() => { save.reset(); del.reset(); }} />}
        <label className="field-label" htmlFor="sv-name">Name</label>
        <input id="sv-name" className="field" style={{ width: '100%' }} value={name} onChange={(e) => setName(e.target.value)} />
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <label className="field-label" htmlFor="sv-sap">SAP</label>
            <input id="sv-sap" className="field" style={{ width: '100%' }} value={sap} onChange={(e) => setSap(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="field-label" htmlFor="sv-delivery">Delivery date</label>
            <input id="sv-delivery" className="field" style={{ width: '100%' }} type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
          </div>
        </div>
        <label className="field-label" htmlFor="sv-notes" style={{ marginTop: 10 }}>Notes</label>
        <input id="sv-notes" className="field" style={{ width: '100%' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <label className="check-row" style={{ display: 'flex', gap: 8, marginTop: 12, fontSize: 12 }}>
          <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} /> Hidden
        </label>
      </Modal>
      {confirmNode}
    </>
  );
}

// ── import from Smartsheet modal ────────────────────────────────────────────────
function ImportModal({ existingSaps, onClose }: { existingSaps: Set<string>; onClose: () => void }) {
  const fmtDate = useFormatDate();
  const isPhone = useIsPhone();
  const { activeSeason } = useSeason();
  const { data, isLoading, error } = useOverview();
  const doImport = useImportSchedule();
  const [q, setQ] = useState('');
  const [autoTasks, setAutoTasks] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initDone, setInitDone] = useState(false);

  // default to selecting every vessel once the overview loads ("import all").
  useEffect(() => {
    if (data && !initDone) {
      setSelected(new Set((data.vessels ?? []).filter((v) => v.sap).map((v) => v.sap)));
      setInitDone(true);
    }
  }, [data, initDone]);

  const vessels = useMemo(() => {
    let vs = (data?.vessels ?? []).filter((v) => v.sap);
    const query = fold(q.trim());
    if (query) vs = vs.filter((v) => hasText(query, v.name, v.sap, v.model));
    return vs;
  }, [data, q]);

  const toggle = (sap: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(sap)) n.delete(sap);
      else n.add(sap);
      return n;
    });
  const allShownSelected = vessels.length > 0 && vessels.every((v) => selected.has(v.sap));
  const toggleAll = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allShownSelected) vessels.forEach((v) => n.delete(v.sap));
      else vessels.forEach((v) => n.add(v.sap));
      return n;
    });

  const result = doImport.data;

  return (
    <Modal
      title="Import vessels from Smartsheet"
      width={820}
      onClose={onClose}
      footer={
        <>
          <label className="muted imp-auto" style={{ marginRight: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={autoTasks} onChange={(e) => setAutoTasks(e.target.checked)} />
            Auto-create date bars (Eng Install / Start-up / Sea Trial / PDI / Delivery)
          </label>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button
            className="btn btn-primary"
            disabled={selected.size === 0 || doImport.isPending}
            onClick={() => doImport.mutate({ saps: [...selected], autoTasks, season: activeSeason })}
          >
            {doImport.isPending ? 'Importing…' : `Import ${selected.size}`}
          </button>
        </>
      }
    >
      {isLoading && <LoadingState label="Loading Overview…" />}
      {error && <ErrorState error={error} />}
      {doImport.isError && <InlineError error={doImport.error} onDismiss={() => doImport.reset()} />}
      {result && (
        <div className="state" style={{ padding: 10, marginBottom: 8, background: 'var(--ok-bg)', borderRadius: 'var(--radius)' }}>
          <div style={{ fontWeight: 600 }}>
            Imported {result.addedVessels} new · updated {result.updatedVessels} · tasks +{result.addedTasks}
            {result.updatedTasks ? ` (${result.updatedTasks} moved)` : ''}{result.removedTasks ? ` · ${result.removedTasks} removed` : ''}
          </div>
        </div>
      )}
      {data && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input className="field" placeholder="Search SAP / vessel / model" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: 1 }} />
            <span className="muted">{selected.size} selected</span>
          </div>
          {isPhone ? (
            // phones: a row per vessel, the whole row toggles it; the sheet body scrolls (no inner scroller)
            <>
              <label className="check-row imp-all">
                <input type="checkbox" checked={allShownSelected} onChange={toggleAll} /> Select all shown
              </label>
              <ul className="imp-list">
                {vessels.map((v) => {
                  const on = selected.has(v.sap);
                  // the dates the import turns into bars; a missing one is left out rather than shown as '-'
                  const dates = [
                    ['Eng', v.engineInstallDate], ['Trial', v.seaTrialDate], ['PDI', v.pdiDate], ['Del', v.deliveryDate],
                  ].map(([k, d]) => (fmtDate(d) ? `${k} ${fmtDate(d)}` : '')).filter(Boolean).join(' · ');
                  return (
                    <li key={v.sap}>
                      <label className={`imp-row${on ? '' : ' is-off'}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(v.sap)} aria-label={`Select ${v.name} (${v.sap})`} />
                        <span className="imp-body">
                          <span className="imp-name">{v.name}</span>
                          <span className="imp-meta">{[v.sap, v.model, existingSaps.has(v.sap) && 'In schedule'].filter(Boolean).join(' · ')}</span>
                          {dates && <span className="imp-dates">{dates}</span>}
                        </span>
                      </label>
                    </li>
                  );
                })}
                {vessels.length === 0 && <li className="muted imp-none">No vessels.</li>}
              </ul>
            </>
          ) : (
          <div className="tbl-wrap" style={{ maxHeight: '52vh', overflow: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 28 }}><input type="checkbox" checked={allShownSelected} onChange={toggleAll} aria-label="Select all shown" /></th>
                  <th>SAP</th>
                  <th>Vessel</th>
                  <th>Eng Inst</th>
                  <th>Sea Trial</th>
                  <th>PDI</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {vessels.map((v) => {
                  const inSched = existingSaps.has(v.sap);
                  return (
                    <tr key={v.sap} style={{ opacity: selected.has(v.sap) ? 1 : 0.55 }}>
                      <td><input type="checkbox" checked={selected.has(v.sap)} onChange={() => toggle(v.sap)} aria-label={`Select ${v.name} (${v.sap})`} /></td>
                      <td>{v.sap}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{v.name}</div>
                        <div style={{ fontSize: 10, opacity: 0.75 }}>
                          {v.model}{inSched && <span className="pill info" style={{ marginLeft: 6 }}>in schedule</span>}
                        </div>
                      </td>
                      <td style={{ fontSize: 11 }}>{fmtDate(v.engineInstallDate) || '-'}</td>
                      <td style={{ fontSize: 11 }}>{fmtDate(v.seaTrialDate) || '-'}</td>
                      <td style={{ fontSize: 11 }}>{fmtDate(v.pdiDate) || '-'}</td>
                      <td style={{ fontSize: 11 }}>{fmtDate(v.deliveryDate) || '-'}</td>
                    </tr>
                  );
                })}
                {vessels.length === 0 && (
                  <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>No vessels.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ── screen ────────────────────────────────────────────────────────────────────
type TaskEdit = { vesselId: string; task: ScheduleTask | null; day: string };

export function Schedule() {
  const { can } = useAuth();
  const canWrite = can('schedule:write');
  const { activeSeason } = useSeason();
  const { data, isLoading, error, refetch } = useSchedule(activeSeason);
  const overview = useOverview();
  const saveTask = useSaveScheduleTask();
  const doImport = useImportSchedule();
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const isPhone = useIsPhone();

  const [view, setView] = useState<SchedView>(initialView);
  const [weekStart, setWeekStart] = useState(() => thisMonday());
  const [q, setQ] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [hideDelivered, setHideDelivered] = useState(false);
  const [hideNoTasksWeek, setHideNoTasksWeek] = useState(false);
  const [visibleTypes, setVisibleTypes] = useState<Set<TaskType>>(() => new Set(TASK_TYPES));
  const [selected, setSelected] = useState<Set<string>>(new Set()); // vessels chosen for export/print
  // phones: the export checkboxes stay hidden until "Select" mode (from ⋯); desktop always shows them
  const [selectMode, setSelectMode] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLOListElement>(null);
  const [taskEdit, setTaskEdit] = useState<TaskEdit | null>(null);
  const [vesselEdit, setVesselEdit] = useState<ScheduleVessel | null | undefined>(undefined); // undefined = closed
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [exportErr, setExportErr] = useState<unknown>(null);
  const [printErr, setPrintErr] = useState<Error | null>(null);

  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const firstDay = days[0] ?? weekStart;
  const lastDay = days[days.length - 1] ?? weekStart;
  const today = localTodayIso();
  const existingSaps = useMemo(
    () => new Set((data?.vessels ?? []).map((v) => v.sap).filter(Boolean)),
    [data],
  );

  const tasksByVessel = useMemo(() => {
    const m = new Map<string, ScheduleTask[]>();
    for (const t of data?.tasks ?? []) {
      const arr = m.get(t.vesselId) ?? [];
      arr.push(t);
      m.set(t.vesselId, arr);
    }
    return m;
  }, [data]);

  // "Hide delivered" hides vessels that have LEFT the yard — i.e. the "Left" column is ticked in the
  // Smartsheet overview (matched by SAP). vessels with left unticked (or with no overview match) stay.
  const leftSaps = useMemo(
    () => new Set((overview.data?.vessels ?? []).filter((v) => v.left).map((v) => String(v.sap).trim())),
    [overview.data],
  );
  const isDeliveredVessel = (v: ScheduleVessel): boolean => leftSaps.has(String(v.sap).trim());

  // An empty selection means "no type filter" (show all) rather than a blank grid — so turning the
  // last type off can never empty the board. `effTypes` is what actually filters; the chips reflect
  // the raw `visibleTypes`, with the "All" chip active whenever nothing is specifically narrowed.
  const effTypes = useMemo(() => (visibleTypes.size ? visibleTypes : new Set(TASK_TYPES)), [visibleTypes]);
  const allTypesVisible = effTypes.size === TASK_TYPES.length;
  const typesFiltered = visibleTypes.size > 0 && visibleTypes.size < TASK_TYPES.length;
  const toggleType = (t: TaskType) =>
    setVisibleTypes((prev) => {
      const n = new Set(prev.size ? prev : TASK_TYPES); // start from "all" when nothing is selected
      if (n.has(t)) n.delete(t);
      else n.add(t);
      return n;
    });

  const vessels = useMemo(() => {
    let vs = data?.vessels ?? [];
    if (!showHidden) vs = vs.filter((v) => !v.hidden);
    if (hideDelivered) vs = vs.filter((v) => !isDeliveredVessel(v));
    // when a subset of task types is on, only show vessels that have a matching task.
    if (!allTypesVisible)
      vs = vs.filter((v) => (tasksByVessel.get(v.id) ?? []).some((t) => effTypes.has(t.type)));
    // hide vessels with no task overlapping any day of the displayed week.
    if (hideNoTasksWeek)
      vs = vs.filter((v) => (tasksByVessel.get(v.id) ?? []).some((t) => days.some((d) => taskCoversDay(t, d))));
    const query = fold(q.trim());
    if (query) vs = vs.filter((v) => hasText(query, v.name, v.sap));
    return vs;
  }, [data, q, showHidden, hideDelivered, hideNoTasksWeek, days, effTypes, allTypesVisible, tasksByVessel, leftSaps]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setQ(''); setHideDelivered(false); setHideNoTasksWeek(false); setShowHidden(false); setVisibleTypes(new Set(TASK_TYPES));
  };
  const activeFilters = (q.trim() ? 1 : 0) + (hideDelivered ? 1 : 0) + (hideNoTasksWeek ? 1 : 0) + (showHidden ? 1 : 0) + (typesFiltered ? 1 : 0);

  // List (agenda) view: every task for the filtered vessels, across all dates, soonest first. Uses
  // the same search / hidden / delivered / task-type filters as the grid, but NOT the week window.
  const listRows = useMemo(() => {
    let vs = data?.vessels ?? [];
    if (!showHidden) vs = vs.filter((v) => !v.hidden);
    if (hideDelivered) vs = vs.filter((v) => !isDeliveredVessel(v));
    const query = fold(q.trim());
    if (query) vs = vs.filter((v) => hasText(query, v.name, v.sap));
    const rows: Array<{ v: ScheduleVessel; t: ScheduleTask }> = [];
    for (const v of vs) {
      for (const t of tasksByVessel.get(v.id) ?? []) {
        if (effTypes.has(t.type)) rows.push({ v, t });
      }
    }
    rows.sort((a, b) => (a.t.start < b.t.start ? -1 : a.t.start > b.t.start ? 1 : a.v.name.localeCompare(b.v.name)));
    return rows;
  }, [data, q, showHidden, hideDelivered, effTypes, tasksByVessel, leftSaps]); // eslint-disable-line react-hooks/exhaustive-deps

  // headline numbers for the week in view, over the vessels currently shown and the task types on.
  const weekStats = useMemo(() => {
    const first = firstDay, last = lastDay;
    let tasksThisWeek = 0, doneThisWeek = 0, delivering = 0;
    for (const v of vessels) {
      if (v.deliveryDate && v.deliveryDate >= first && v.deliveryDate <= last) delivering += 1;
      for (const t of tasksByVessel.get(v.id) ?? []) {
        if (!effTypes.has(t.type)) continue;
        if (!days.some((d) => taskCoversDay(t, d))) continue;
        tasksThisWeek += 1;
        if (t.completed) doneThisWeek += 1;
      }
    }
    return { tasksThisWeek, doneThisWeek, delivering };
  }, [vessels, tasksByVessel, effTypes, days, firstDay, lastDay]);
  const isThisWeek = weekStart === thisMonday();

  const toggleComplete = (t: ScheduleTask) =>
    canWrite && saveTask.mutate({ id: t.id, vesselId: t.vesselId, type: t.type, start: t.start, end: t.end, notes: t.notes, completed: !t.completed });

  // ── export/print selection ──────────────────────────────────────────────────────
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allShownSelected = vessels.length > 0 && vessels.every((v) => selected.has(v.id));
  const toggleSelectAllShown = () =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (allShownSelected) vessels.forEach((v) => n.delete(v.id));
      else vessels.forEach((v) => n.add(v.id));
      return n;
    });
  // export the chosen vessels; if none chosen, fall back to everything currently shown.
  const exportVessels = selected.size ? vessels.filter((v) => selected.has(v.id)) : vessels;
  const showChecks = !isPhone || selectMode;
  // leaving Select mode drops the selection, so a hidden tick can't quietly narrow a later export
  const endSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const chooseView = (v: SchedView) => {
    setView(v);
    rememberView(v);
    if (v !== 'week' && selectMode) endSelect();
  };
  // a phone wraps the export checkbox in a 44px label; desktop keeps the bare checkbox
  const selBox = (input: ReactNode) => (isPhone ? <label className="sched-chk">{input}</label> : input);

  // phones: the board scrolls sideways, so bring today's column in beside the pinned vessel column on
  // open and on every week change (a week without today starts at Monday).
  const hasData = !!data;
  useEffect(() => {
    const wrap = boardRef.current;
    if (!isPhone || view !== 'week' || !wrap) return;
    const pin = wrap.querySelector('th.vh');
    const now = wrap.querySelector('th.today');
    wrap.scrollLeft = pin && now ? wrap.scrollLeft + now.getBoundingClientRect().left - pin.getBoundingClientRect().right : 0;
  }, [isPhone, view, weekStart, hasData]);

  // List view on phones: the agenda grouped under each start date ("Mon 05 Oct").
  const listGroups = useMemo(() => {
    const out: Array<{ day: string; rows: typeof listRows }> = [];
    for (const r of listRows) {
      const last = out[out.length - 1];
      if (last && last.day === r.t.start) last.rows.push(r);
      else out.push({ day: r.t.start, rows: [r] });
    }
    return out;
  }, [listRows]);
  // the first date that still has a task running today or later. The phone list opens at the top (so the
  // title, view switch and search stay on screen) and a "Jump to today" chip scrolls the page there, its
  // sticky date heading landing at the top, instead of the reader paging past long-finished tasks.
  const nowDay = listGroups.find((g) => g.rows.some(({ t }) => t.end >= today))?.day;
  const canJumpToNow = !!nowDay && nowDay !== listGroups[0]?.day;
  const jumpToNow = () => {
    const at = cardsRef.current?.querySelector<HTMLElement>('[data-now]');
    const sc = cardsRef.current?.closest<HTMLElement>('.content');
    if (!at || !sc) return;
    sc.scrollTo({ top: sc.scrollTop + at.getBoundingClientRect().top - sc.getBoundingClientRect().top });
  };
  const dayHeading = (iso: string) => df.date(iso, { weekday: 'short', year: 'auto' });
  // a week column: the weekday, then the day without its year
  const dayShort = (iso: string) => df.date(iso, { weekday: 'short', year: 'never' });

  // ── exports (respect the current vessel + task-type filters + explicit selection) ─
  const buildRows = (): SchedRow[] => {
    const rows: SchedRow[] = [];
    for (const v of exportVessels) {
      const ts = (tasksByVessel.get(v.id) ?? [])
        .filter((t) => effTypes.has(t.type))
        .sort((a, b) => (a.start < b.start ? -1 : 1));
      if (!ts.length) {
        rows.push({ vessel: v.name, sap: v.sap, delivery: v.deliveryDate, task: '', start: '', end: '', days: '', notes: '', status: '' });
      } else {
        for (const t of ts) {
          rows.push({
            vessel: v.name, sap: v.sap, delivery: v.deliveryDate, task: TASK_LABELS[t.type],
            start: t.start, end: t.end, days: String(daySpan(t.start, t.end)), notes: t.notes ?? '', status: t.completed ? 'Done' : '',
          });
        }
      }
    }
    return rows;
  };
  // the file name stays ISO (it sorts, and "/" can't go in a file name); the heading is in the user's format
  const exportFile = `Schedule ${today}`;
  const exportHeading = `Schedule ${df.date(today)}`;

  const buildPrintHtml = (): string => {
    const head = `<tr><th style="width:16%">Vessel</th>${days.map((d) => `<th>${esc(dayShort(d))}</th>`).join('')}</tr>`;
    const body = exportVessels.map((v) => {
      const tasks = (tasksByVessel.get(v.id) ?? []).filter((t) => effTypes.has(t.type));
      const cells = days.map((d) => {
        const bars = tasks.filter((t) => taskCoversDay(t, d)).map((t) => `<span class="bar ${t.completed ? 'done' : ''}">${esc(TASK_LABELS[t.type])}</span>`).join('');
        return `<td>${bars}</td>`;
      }).join('');
      return `<tr><td><b>${esc(v.name)}</b><br><small>${esc(v.sap)}${v.deliveryDate ? ` · DEL ${esc(df.date(v.deliveryDate) || v.deliveryDate)}` : ''}</small></td>${cells}</tr>`;
    }).join('');
    const typeSub = allTypesVisible ? '' : ` · ${[...effTypes].map((t) => TASK_LABELS[t]).join(', ') || 'no tasks'}`;
    return `<h1>Schedule</h1><div class="sub">${esc(df.range(firstDay, lastDay))}${typeSub} · Rolodex</div><table><thead>${head}</thead><tbody>${body}</tbody></table>`;
  };

  // the xlsx / jsPDF chunks load lazily, so a failed fetch surfaces here rather than as a silent rejection
  const doExport = async (kind: 'xlsx' | 'pdf') => {
    setExporting(kind); setExportErr(null);
    try { await (kind === 'xlsx' ? exportScheduleXlsx(buildRows(), exportFile, df.prefs) : exportSchedulePdf(buildRows(), exportFile, df.prefs, exportHeading)); }
    catch (e) { setExportErr(e); }
    finally { setExporting(null); }
  };
  const doPrint = () =>
    setPrintErr(printSchedule('Schedule', buildPrintHtml()) ? null : new Error('The print window was blocked. Allow pop-ups for this site and try again.'));

  // header actions: inline buttons on wider screens; on phones they fold into one ⋯ sheet ('+ Vessel' stays)
  const syncAll = () => {
    const saps = [...existingSaps];
    if (saps.length === 0) { void refetch(); void overview.refetch(); return; }
    doImport.mutate(
      { saps, autoTasks: true, season: activeSeason },
      { onSuccess: () => { void refetch(); void overview.refetch(); } },
    );
  };
  const ioActions: Array<OverflowAction | false> = [
    canWrite && { label: 'Import', icon: <IconUpload size={14} />, onClick: () => setImportOpen(true) },
    canWrite && {
      label: doImport.isPending ? 'Syncing…' : 'Sync',
      icon: doImport.isPending ? <LogoSpin size={15} decorative /> : undefined,
      disabled: doImport.isPending,
      title: 'Re-pull the Smartsheet date bars (Engine Install, Start-up, Sea Trial, PDI, Delivery) for every scheduled vessel, then refresh',
      onClick: syncAll,
    },
  ];
  const exportActions: OverflowAction[] = [
    { label: exporting === 'xlsx' ? 'Exporting…' : 'Excel', icon: exporting === 'xlsx' ? undefined : <IconDownload size={14} />, disabled: exporting !== null, onClick: () => void doExport('xlsx') },
    { label: exporting === 'pdf' ? 'Exporting…' : 'PDF', icon: exporting === 'pdf' ? undefined : <IconDownload size={14} />, disabled: exporting !== null, onClick: () => void doExport('pdf') },
    { label: 'Print', icon: <IconPrinter size={14} />, onClick: doPrint },
  ];
  const selectAction: OverflowAction | false = view === 'week' && !selectMode && {
    label: 'Select vessels to export', icon: <IconCheck size={14} />, onClick: () => setSelectMode(true),
  };

  return (
    <>
      <style>{SCHED_CSS}</style>
      <PageHeader title="Schedule">
        <Segmented value={view} onChange={chooseView} ariaLabel="View" options={[{ value: 'week', label: 'Week' }, { value: 'list', label: 'List' }]} />
        {view === 'week' && (
          <div className="sched-weeknav">
            <button className="btn btn-sm" title="Previous week" aria-label="Previous week" onClick={() => setWeekStart((w) => shiftWeek(w, -7))}>‹</button>
            <span className="wk-range">
              {df.range(firstDay, lastDay)}
              {isThisWeek && <span className="wk-range-now">This week</span>}
            </span>
            <button className="btn btn-sm" title="Next week" aria-label="Next week" onClick={() => setWeekStart((w) => shiftWeek(w, 7))}>›</button>
            <button className="btn btn-sm" onClick={() => setWeekStart(thisMonday())}>Today</button>
            {isThisWeek && <span className="wk-now">This week</span>}
          </div>
        )}
        {canWrite && <button className="btn btn-sm btn-primary" onClick={() => setVesselEdit(null)}>+ Vessel</button>}
        <OverflowActions items={isPhone ? [...ioActions, ...exportActions, selectAction] : ioActions} title="Schedule" />
        {isPhone && (doImport.isPending || exporting) && <span className="muted" role="status"><LogoSpin size={15} decorative /> {doImport.isPending ? 'Syncing…' : 'Exporting…'}</span>}
        {doImport.isError && <span className="muted" style={{ color: 'var(--err-fg)' }}>Sync failed</span>}
        {!isPhone && selected.size > 0 && <span className="muted" style={{ fontSize: 11 }} title="Tick vessels to export a subset; none ticked exports all shown">{selected.size} selected</span>}
        {!isPhone && <OverflowActions items={exportActions} />}
      </PageHeader>
      {exportErr ? <InlineError error={exportErr} onDismiss={() => setExportErr(null)} /> : null}
      {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}

      {data && view === 'week' && (
        // the week in view as one sentence: vessels on the board (neutral), the week's tasks (current, info
        // blue), the ones done (ok green) and the boats whose delivery date falls in the week (accent)
        <FactSentence className="sched-facts">
          The board shows <Fact title="Vessels on the board with the current filters">{vessels.length} {vessels.length === 1 ? 'vessel' : 'vessels'}</Fact>{' '}
          with{' '}
          {weekStats.tasksThisWeek
            ? <Fact tone="info" title="Tasks running on at least one day of the week in view">{weekStats.tasksThisWeek} {weekStats.tasksThisWeek === 1 ? 'task' : 'tasks'}</Fact>
            : <Fact title="Tasks running on at least one day of the week in view">no tasks</Fact>}{' '}
          {isThisWeek ? 'this week' : 'that week'}<FactEnd>,</FactEnd>{' '}
          <Fact tone={weekStats.doneThisWeek ? 'ok' : undefined} title="Tasks in the week in view ticked done">{weekStats.doneThisWeek} done</Fact>{' '}
          {isThisWeek ? 'so far ' : ''}and{' '}
          <Fact tone={weekStats.delivering ? 'acc' : undefined} title="Vessels whose delivery date falls in the week in view">{weekStats.delivering} {weekStats.delivering === 1 ? 'boat' : 'boats'} delivering</Fact><FactEnd>.</FactEnd>
        </FactSentence>
      )}

      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder="Search vessel / SAP" width={200} />} activeCount={activeFilters} onClear={clearFilters}>
        <FilterToggle checked={hideDelivered} onChange={setHideDelivered} title="Hides vessels whose Left column is ticked in the Smartsheet Overview">Hide delivered</FilterToggle>
        {view === 'week' && <FilterToggle checked={hideNoTasksWeek} onChange={setHideNoTasksWeek} title="Show only vessels with a task in the week on screen">With tasks this week</FilterToggle>}
        <FilterToggle checked={showHidden} onChange={setShowHidden}>Show hidden</FilterToggle>
        <ChipGroup label="Tasks">
          <Chip active={allTypesVisible} onClick={() => setVisibleTypes(new Set(TASK_TYPES))}>All</Chip>
          {TASK_TYPES.map((t) => (
            <Chip key={t} active={allTypesVisible || visibleTypes.has(t)} dot={`var(--task-${t})`} onClick={() => toggleType(t)} ariaLabel={TASK_LABELS[t]}>{TASK_LABELS[t]}</Chip>
          ))}
        </ChipGroup>
      </FilterBar>
      {saveTask.isError && <InlineError error={saveTask.error} onDismiss={() => saveTask.reset()} />}

      {isLoading && <LoadingState label="Loading schedule…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && view === 'week' && (
        <div ref={boardRef} className={`tbl-wrap is-grid sched-board${selectMode ? ' is-selecting' : ''}`}>
          <table className="gantt-tbl">
            <thead>
              <tr>
                <th className="vh pin-col">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {showChecks && selBox(
                      <input
                        type="checkbox"
                        checked={allShownSelected}
                        onChange={toggleSelectAllShown}
                        title="Select all shown for export/print"
                        aria-label={isPhone ? 'Select all shown' : undefined}
                        style={{ cursor: 'pointer' }}
                      />,
                    )}
                    Vessel
                  </span>
                </th>
                {days.map((d, i) => (
                  <th key={d} className={`${d === today ? 'today' : ''} ${i >= 5 ? 'wknd' : ''}`}>
                    {df.weekday(d)}<br />{df.date(d, { year: 'never' })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vessels.map((v) => {
                const tasks = tasksByVessel.get(v.id) ?? [];
                // in Select mode (phones) a tap anywhere on the row ticks it instead of opening a dialog
                const vesselTap = selectMode ? () => toggleSelect(v.id) : canWrite ? () => setVesselEdit(v) : undefined;
                return (
                  <tr key={v.id}>
                    <td className="vcell pin-col">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        {showChecks && selBox(
                          <input
                            type="checkbox"
                            checked={selected.has(v.id)}
                            onChange={() => toggleSelect(v.id)}
                            onClick={(e) => e.stopPropagation()}
                            title="Include in export/print"
                            aria-label={isPhone ? `Select ${v.name}` : undefined}
                            style={{ marginTop: 2, cursor: 'pointer' }}
                          />,
                        )}
                        <div
                          style={{ flex: 1, minWidth: 0, cursor: vesselTap ? 'pointer' : 'default' }}
                          onClick={vesselTap}
                          title={selectMode ? '' : canWrite ? 'Edit vessel' : ''}
                          role={vesselTap ? 'button' : undefined}
                          tabIndex={vesselTap ? 0 : undefined}
                          aria-label={selectMode ? `Select ${v.name}` : canWrite ? `Edit vessel ${v.name}` : undefined}
                          aria-pressed={selectMode ? selected.has(v.id) : undefined}
                          onKeyDown={vesselTap ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); vesselTap(); }
                          } : undefined}
                        >
                          <div className="pin-clamp" style={{ fontWeight: 600 }}>{v.name}</div>
                          <div className="v-meta">
                            {v.sap}{v.deliveryDate ? ` · DEL ${fmtDate(v.deliveryDate)}` : ''}
                          </div>
                          {v.notes && <div className="sched-vnote">{v.notes}</div>}
                        </div>
                      </div>
                    </td>
                    {days.map((d, i) => {
                      const bars = tasks.filter((t) => taskCoversDay(t, d) && effTypes.has(t.type));
                      return (
                        <td
                          key={d}
                          className={`dcell ${d === today ? 'today' : ''} ${i >= 5 ? 'wknd' : ''}`}
                          onClick={() => {
                            if (selectMode) toggleSelect(v.id);
                            else if (canWrite) setTaskEdit({ vesselId: v.id, task: null, day: d });
                          }}
                          tabIndex={canWrite && bars.length === 0 ? 0 : undefined}
                          aria-label={canWrite && bars.length === 0 ? `Add task - ${v.name}, ${dayShort(d)}` : undefined}
                          onKeyDown={canWrite && bars.length === 0 ? (e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                              e.preventDefault();
                              setTaskEdit({ vesselId: v.id, task: null, day: d });
                            }
                          } : undefined}
                        >
                          {bars.map((t) => {
                            const isStart = t.start === d;
                            const isEnd = t.end === d;
                            const cls = [
                              'tbar', t.type, t.completed ? 'completed' : '',
                              isStart ? '' : 'cont',
                              isStart && !isEnd ? 'r-start' : '',
                              !isStart && isEnd ? 'r-end' : '',
                              !isStart && !isEnd ? 'r-mid' : '',
                            ].filter(Boolean).join(' ');
                            // one tab stop per task per row: its start day, or Monday for a carried-over task
                            const focusable = canWrite && (isStart || i === 0);
                            return (
                              <span
                                key={t.id}
                                className={cls}
                                title={`${TASK_LABELS[t.type]}${t.notes ? ': ' + t.notes : ''}`}
                                role={focusable ? 'button' : undefined}
                                tabIndex={focusable ? 0 : undefined}
                                onKeyDown={focusable ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setTaskEdit({ vesselId: v.id, task: t, day: d });
                                  }
                                } : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (selectMode) toggleSelect(v.id);
                                  else if (canWrite) setTaskEdit({ vesselId: v.id, task: t, day: d });
                                }}
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  if (!selectMode) toggleComplete(t);
                                }}
                              >
                                {isStart && (
                                  <>
                                    {TASK_LABELS[t.type]}
                                    {t.notes && <span className="tbar-note">{t.notes}</span>}
                                  </>
                                )}
                              </span>
                            );
                          })}
                          {canWrite && bars.length === 0 && <span className="dcell-add">+</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* the empty state sits under the board, not centred in a row of a table wider than a phone */}
      {data && view === 'week' && vessels.length === 0 && (
        <div className="grid-empty muted">
          {(data?.vessels.length ?? 0) === 0 ? (
            <>
              No vessels for season {activeSeason} yet.{' '}
              {canWrite && <button className="btn btn-sm" onClick={() => setImportOpen(true)}>Import from Smartsheet</button>}
            </>
          ) : (
            <>
              No vessels match the filters.{' '}
              {activeFilters > 0 && <button className="btn btn-sm btn-ghost" onClick={clearFilters}>Clear filters</button>}
            </>
          )}
        </div>
      )}

      {data && view === 'list' && (
        <>
          <div className={`muted${isPhone ? ' sched-list-cap' : ''}`} style={{ margin: '0 0 8px', fontSize: 12 }}>
            {listRows.length} task{listRows.length === 1 ? '' : 's'} across all dates
            {isPhone && canJumpToNow && (
              <button type="button" className="fchip sched-jump" onClick={jumpToNow}>Jump to today<span aria-hidden="true">↓</span></button>
            )}
          </div>
          {isPhone ? (
            listRows.length === 0 ? (
              <div className="grid-empty muted">
                {(data?.vessels.length ?? 0) === 0 ? (
                  <>No vessels for season {activeSeason} yet. {canWrite && <button className="btn btn-sm" onClick={() => setImportOpen(true)}>Import from Smartsheet</button>}</>
                ) : (
                  <>No tasks match the filters.{activeFilters > 0 && <button className="btn btn-sm btn-ghost" onClick={clearFilters}>Clear filters</button>}</>
                )}
              </div>
            ) : (
              <ol ref={cardsRef} className="sched-cards" aria-label="Tasks by start date">
                {listGroups.map((g) => (
                  <li key={g.day} className="sched-day" data-now={g.day === nowDay ? '' : undefined}>
                    <h2 className="sched-day-hd">
                      {dayHeading(g.day)}
                      {g.day === today && <span className="sched-day-now">Today</span>}
                    </h2>
                    <ol className="sched-day-list">
                      {g.rows.map(({ v, t }) => {
                        const isToday = t.start <= today && today <= t.end;
                        const span = daySpan(t.start, t.end);
                        const busy = saveTask.isPending && saveTask.variables?.id === t.id;
                        const body = (
                          <>
                            <span className="sched-card-task">
                              <span className="fchip-dot" style={{ background: `var(--task-${t.type})` }} />
                              {TASK_LABELS[t.type]}
                            </span>
                            <span className="sched-card-vessel">{v.name}</span>
                            <span className="sched-card-meta">{[v.sap, `${span} day${span === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}</span>
                            {t.notes && <span className="sched-card-notes">{t.notes}</span>}
                          </>
                        );
                        return (
                          <li key={t.id} className={`sched-card${isToday ? ' is-today' : ''}${t.completed ? ' is-done' : ''}`}>
                            {canWrite ? (
                              <button type="button" className="sched-card-main" onClick={() => setTaskEdit({ vesselId: v.id, task: t, day: t.start })}>{body}</button>
                            ) : (
                              <div className="sched-card-main">{body}</div>
                            )}
                            <button
                              type="button"
                              className="sched-done"
                              aria-pressed={t.completed}
                              aria-busy={busy || undefined}
                              aria-label={`Done - ${TASK_LABELS[t.type]}, ${v.name}`}
                              title="Mark done"
                              disabled={!canWrite || busy}
                              onClick={() => toggleComplete(t)}
                            >
                              <IconCheck size={20} />
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                  </li>
                ))}
              </ol>
            )
          ) : (
          <div className="tbl-wrap">
            <table className="tbl sched-list">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Date</th>
                  <th>Task</th>
                  <th>Vessel</th>
                  <th style={{ width: 60 }}>Days</th>
                  <th>Notes</th>
                  <th style={{ width: 64 }}>Done</th>
                </tr>
              </thead>
              <tbody>
                {listRows.map(({ v, t }) => {
                  const wd = df.weekday(t.start);
                  const isToday = t.start <= today && today <= t.end;
                  const openTask = () => setTaskEdit({ vesselId: v.id, task: t, day: t.start });
                  const labelStyle = { textDecoration: t.completed ? 'line-through' : 'none', opacity: t.completed ? 0.6 : 1 };
                  return (
                    <tr
                      key={t.id}
                      style={{ cursor: canWrite ? 'pointer' : 'default', background: isToday ? 'var(--accent-weak)' : undefined }}
                      // mouse convenience only — the task label button below is the keyboard / screen-reader
                      // control, so the row keeps its table semantics and the Done checkbox stays its own control
                      onClick={() => canWrite && openTask()}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}><span className="muted" style={{ marginRight: 6 }}>{wd}</span>{fmtDate(t.start)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className="fchip-dot" style={{ background: `var(--task-${t.type})`, display: 'inline-block', marginRight: 6, verticalAlign: 'middle', opacity: 1 }} />
                        {canWrite ? (
                          <button
                            type="button"
                            className="sl-open"
                            style={labelStyle}
                            aria-label={`Edit task - ${TASK_LABELS[t.type]}, ${v.name}`}
                            onClick={(e) => { e.stopPropagation(); openTask(); }}
                          >{TASK_LABELS[t.type]}</button>
                        ) : (
                          <span style={labelStyle}>{TASK_LABELS[t.type]}</span>
                        )}
                      </td>
                      <td><span style={{ fontWeight: 600 }}>{v.name}</span>{v.sap && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{v.sap}</span>}</td>
                      <td>{daySpan(t.start, t.end)}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{t.notes}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={t.completed} disabled={!canWrite} onChange={() => toggleComplete(t)} title="Mark done" style={{ cursor: canWrite ? 'pointer' : 'default' }} />
                      </td>
                    </tr>
                  );
                })}
                {listRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 22 }}>
                      {(data?.vessels.length ?? 0) === 0 ? (
                        <>No vessels for season {activeSeason} yet. {canWrite && <button className="btn btn-sm" style={{ marginLeft: 4 }} onClick={() => setImportOpen(true)}>Import from Smartsheet</button>}</>
                      ) : (
                        <>No tasks match the filters.{activeFilters > 0 && <button className="btn btn-sm btn-ghost" style={{ marginLeft: 4 }} onClick={clearFilters}>Clear filters</button>}</>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}

      {taskEdit && (
        <TaskModal vesselId={taskEdit.vesselId} task={taskEdit.task} day={taskEdit.day} onClose={() => setTaskEdit(null)} />
      )}
      {vesselEdit !== undefined && <VesselModal vessel={vesselEdit} season={activeSeason} onClose={() => setVesselEdit(undefined)} />}
      {importOpen && <ImportModal existingSaps={existingSaps} onClose={() => setImportOpen(false)} />}
      {/* phones, Select mode: the export actions ride at the bottom, in thumb reach; none ticked exports all shown */}
      <ActionBar show={isPhone && selectMode} message={selected.size ? `${selected.size} selected` : 'Tap vessels to select'}>
        <button className="btn btn-sm" disabled={exporting !== null} onClick={() => void doExport('xlsx')}>{exporting === 'xlsx' ? '…' : 'Excel'}</button>
        <button className="btn btn-sm" disabled={exporting !== null} onClick={() => void doExport('pdf')}>{exporting === 'pdf' ? '…' : 'PDF'}</button>
        <button className="btn btn-sm" onClick={doPrint}>Print</button>
        <button className="btn btn-sm btn-primary" onClick={endSelect}>Done</button>
      </ActionBar>
    </>
  );
}
